import { api } from '@/api/request';

export type WorkflowExecutionStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped'
  | 'cancelled';

export type WorkflowExecutionSubject = {
  type: string;
  id: number;
  name?: string | null;
  url?: string | null;
  avatar_url?: string | null;
  // BE nests the candidate's avatar + id here (`subject.contact.image_url`).
  contact?: {
    id?: number;
    image_url?: string | null;
  } | null;
};

// Reviewer / hiring-manager recipient, per BE `next_action.recipients`.
export type WorkflowExecutionRecipient = {
  id?: number;
  name?: string | null;
  avatar_url?: string | null;
};

export type WorkflowExecutionNextAction = {
  stage_action_id: number;
  label: string;
  kind: string;
  position: number;
  delay_seconds: number;
  // True when the underlying `AtsApplicationStageAction` record already exists
  // for (application, stage_action) — the BE mirrors its own
  // `ExecuteStageAction.already_created?` predicate here. The run would
  // `already_created`-skip this step, so the popup must NOT re-show it on stage
  // re-entry (move out + back). This is the authoritative re-entry signal — the
  // execution-derived `status` can't provide it, because a fresh re-entry
  // execution reads `pending` until it's actually triggered. See
  // `firstStepAlreadyRan`.
  already_ran?: boolean;
  // Kind-specific enrichment (BE `Workflow::Ats::Helpers#next_action_info`),
  // present only for the relevant kinds:
  //   interview → automation_mode
  //   survey    → survey_type (+ recipients/recipient_kind when employee)
  //   review    → recipients + recipient_kind
  automation_mode?: string;
  survey_type?: 'candidate' | 'employee';
  recipient_kind?: 'reviewer' | 'user';
  recipients?: WorkflowExecutionRecipient[];
};

export type WorkflowExecutionSummary = {
  id: number;
  status: WorkflowExecutionStatus;
  scheduled_at: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  attempts?: number;
  skip_reason?: string | null;
  last_error?: string | null;
  created_at?: string;
  subject?: WorkflowExecutionSubject;
  workflow_rule?: {
    id: number;
    name?: string;
    event_name?: string;
    trigger_config?: { ats_job_stage_id?: number };
    // Job/stage context for the row — BE `_workflow_rule` → `summary`.
    summary?: {
      kind?: string;
      ats_job?: { id: number; name: string } | null;
      ats_job_stage?: { id: number; name: string } | null;
    };
    actions?: Array<{
      id?: number;
      position?: number;
      // `esign_documents` is the stage action's configured set, rendered here
      // by the target's full-details partial. Its length pluralises the row's
      // copy; always an array when `target` is present, empty when there are
      // none. `target` itself is absent when the stage action was hard-deleted.
      target?: {
        kind?: string;
        label?: string;
        esign_documents?: unknown[];
      } | null;
    }>;
  };
  next_action?: WorkflowExecutionNextAction;
};

type ExecutionsListResponse = {
  executions: WorkflowExecutionSummary[];
  dispatch_ready?: boolean;
};

export const searchLatestExecutionsForApplications = async (
  applicationIds: Array<string | number>,
  status?: WorkflowExecutionStatus
): Promise<WorkflowExecutionSummary[]> => {
  if (applicationIds.length === 0) return [];
  const res = await api.post<ExecutionsListResponse>(
    'workflow_executions/search',
    {
      subject_ids: applicationIds.map((id) => Number(id)),
      subject_type: 'AtsApplication',
      ...(status ? { status } : {}),
    }
  );
  return res.executions ?? [];
};

export const fetchPendingExecutionsForApplications = (
  applicationIds: Array<string | number>
): Promise<WorkflowExecutionSummary[]> =>
  searchLatestExecutionsForApplications(applicationIds, 'pending');

// GET /workflow_executions?ats_job_id=...&status[]=pending&status[]=running —
// every active automation across a whole job. Used by the close-job warning to
// list the candidates whose automations will be cancelled. The ATS http client
// (qs `arrayFormat: 'brackets'`) serializes the `status` array as
// status[]=pending&status[]=running.
export const fetchActiveExecutionsForJob = async (
  jobId: string | number,
  statuses: WorkflowExecutionStatus[] = ['pending', 'running']
): Promise<WorkflowExecutionSummary[]> => {
  if (!jobId) return [];
  const res = await api.get<ExecutionsListResponse>('workflow_executions', {
    params: { ats_job_id: Number(jobId), status: statuses },
  });
  return res.executions ?? [];
};

export type DispatchSessionSearchResult = {
  executions: WorkflowExecutionSummary[];
  dispatchReady: boolean;
};

// POST /workflow_executions/search scoped to one bulk-move dispatch session.
export const searchExecutionsByDispatchSession = async (
  dispatchSessionId: string
): Promise<DispatchSessionSearchResult> => {
  const res = await api.post<ExecutionsListResponse>(
    'workflow_executions/search',
    { dispatch_session_id: dispatchSessionId }
  );
  return {
    executions: res.executions ?? [],
    dispatchReady: res.dispatch_ready ?? false,
  };
};

type BulkTriggerResponse = {
  status: 'triggered';
  triggered_ids: number[];
  skipped_ids: number[];
};
export const triggerExecutionsBulk = async (
  executionIds: number[]
): Promise<BulkTriggerResponse> => {
  return await api.post<BulkTriggerResponse>(
    'workflow_executions/bulk_trigger',
    { execution_ids: executionIds }
  );
};

export type BulkCancelResponse = {
  status: 'cancelled';
  cancelled_ids: number[];
  skipped_ids: number[];
};
export const cancelExecutionsBulk = async (
  executionIds: number[]
): Promise<BulkCancelResponse> => {
  return await api.post<BulkCancelResponse>('workflow_executions/bulk_cancel', {
    execution_ids: executionIds,
  });
};
